import { Group, Mesh, Object3D } from 'three';
import type { CharacterInstance, CharacterLoader } from '../character/CharacterModel';
import { HAND_R_SOCKET, WEAPON_GRIPS, formatAttachments, saveAttachmentOverrides } from './attachments';

/**
 * Zbraň v ruce postavy.
 *
 * Model visí na kosti `hand_r` a animace ho vozí sama. Umístění se skládá
 * ze dvou nezávislých částí — socketu ruky a gripu zbraně, viz
 * `attachments.ts`. Díky tomu stačí u další zbraně nastavit jedno číslo
 * místo celé sady, a všechny sedí v ruce stejně.
 */
export class Weapon {
  private readonly holder = new Group();
  private current: Object3D | null = null;
  private attachedTo: Object3D | null = null;
  private modelName = 'Sword_Golden';

  constructor(private readonly loader: CharacterLoader) {
    this.holder.name = 'weapon';
    this.holder.visible = false;
  }

  get visible(): boolean {
    return this.holder.visible;
  }

  /**
   * Připne zbraň na pravou ruku.
   *
   * Kost se hledá jménem, protože se po převlečení outfitu mění celý strom
   * meshů, ale kostra zůstává — držet si odkaz na objekt by po přezbrojení
   * ukazovalo do prázdna.
   */
  attach(character: CharacterInstance): boolean {
    const hand = character.root.getObjectByName('hand_r');
    if (!hand) {
      console.warn('[weapon] kost hand_r nenalezena');
      return false;
    }
    if (this.attachedTo === hand) return true;

    hand.add(this.holder);
    this.attachedTo = hand;
    this.applyFit();
    return true;
  }

  async setModel(name = 'Sword_Golden'): Promise<void> {
    const gltf = await this.loader.loadGLTF('/models/combat/weapons.glb');
    const source = gltf.scene.getObjectByName(name);
    if (!source) {
      console.warn(`[weapon] ${name} není v weapons.glb`);
      return;
    }

    this.current?.removeFromParent();

    this.modelName = name;
    const weapon = source.clone(true);
    weapon.position.set(0, 0, WEAPON_GRIPS[name]?.gripOffset ?? 0);
    weapon.rotation.set(0, 0, 0);
    weapon.scale.setScalar(1);
    weapon.traverse((child) => {
      if (child instanceof Mesh) {
        child.castShadow = true;
        child.receiveShadow = true;
        child.frustumCulled = false;
      }
    });

    this.holder.add(weapon);
    this.current = weapon;
    this.applyFit();
  }

  show(visible: boolean): void {
    this.holder.visible = visible;
  }

  applyFit(): void {
    const grip = WEAPON_GRIPS[this.modelName];
    this.holder.position.copy(HAND_R_SOCKET.offset);
    this.holder.rotation.copy(HAND_R_SOCKET.rotation);
    this.holder.scale.setScalar(grip?.scale ?? 1);
    if (this.current) this.current.position.set(0, 0, grip?.gripOffset ?? 0);
  }

  /**
   * Živé ladění posazení. Používá ho panel pod F9.
   *
   * Rotace a posun míří do **socketu ruky** (platí pro všechny zbraně),
   * `grip` a `scale` do **gripu této zbraně**. Hodnoty se rovnou ukládají,
   * aby přežily načtení stránky.
   */
  tune(values: Partial<Record<'x' | 'y' | 'z' | 'rx' | 'ry' | 'rz' | 'scale' | 'grip', number>>): string {
    if (values.x !== undefined) HAND_R_SOCKET.offset.x = values.x;
    if (values.y !== undefined) HAND_R_SOCKET.offset.y = values.y;
    if (values.z !== undefined) HAND_R_SOCKET.offset.z = values.z;
    if (values.rx !== undefined) HAND_R_SOCKET.rotation.x = values.rx;
    if (values.ry !== undefined) HAND_R_SOCKET.rotation.y = values.ry;
    if (values.rz !== undefined) HAND_R_SOCKET.rotation.z = values.rz;

    const grip = (WEAPON_GRIPS[this.modelName] ??= { gripOffset: 0, scale: 1 });
    if (values.grip !== undefined) grip.gripOffset = values.grip;
    if (values.scale !== undefined) grip.scale = values.scale;

    this.applyFit();
    saveAttachmentOverrides();
    return formatAttachments(this.modelName);
  }

  dispose(): void {
    this.holder.removeFromParent();
    this.current = null;
    this.attachedTo = null;
  }
}
